import { chakra } from "@chakra-ui/react";
import SafeHTML from "../../../shared/components/SafeHTML";
import { replacePlaceholders } from "../layout/documentLayout";
import { firstAnswer } from "./answerFormat";
import { BODY_DRAWING_LEGEND_URL, bodyDrawingImages, stripUnwantedStyles } from "./blockFormat";
import { BarTitle, InlineTitle, PrintBlock } from "./printStyles";
import type { QuestionViewProps } from "./types";

// image, bodyDrawing, editor -> the PDF's Image / Column-of-images / PlainText(HTML) blocks.

const PrintImage = chakra("img", {
  baseStyle: {
    maxHeight: "512px",
    maxWidth: "100%",
    my: "4px",
  },
});

function CaptionedImage(props: { caption?: string; url: string }) {
  return (
    <chakra.div display="flex" flex={1} flexDirection="column" minWidth={0}>
      {props.caption !== undefined && <InlineTitle>{props.caption}</InlineTitle>}
      <PrintImage alt={props.caption ?? ""} src={props.url} />
    </chakra.div>
  );
}

export function ImageQuestionView({ block, placeholders }: QuestionViewProps) {
  const { url } = block.question;
  // Like the PDF, an image question without a URL prints nothing.
  if (url === null || url === undefined || url === "") {
    return null;
  }

  return (
    <PrintBlock>
      <CaptionedImage caption={replacePlaceholders(block.question.label, placeholders)} url={url} />
    </PrintBlock>
  );
}

export function BodyDrawingQuestionView({ block }: QuestionViewProps) {
  const images = bodyDrawingImages(firstAnswer(block.answers));

  return (
    <PrintBlock display="flex" flexDirection="column" gap="16px">
      <BarTitle>Body drawing</BarTitle>
      <CaptionedImage url={BODY_DRAWING_LEGEND_URL} />
      <chakra.div display="flex" gap="16px">
        {images.map((image) => (
          <CaptionedImage key={image.caption} caption={image.caption} url={image.url} />
        ))}
      </chakra.div>
    </PrintBlock>
  );
}

const EditorHtml = chakra("div", {
  baseStyle: {
    overflowWrap: "anywhere",
    "& img": { maxWidth: "100%" },
    "& p": { margin: "0 0 0.5em" },
    "& table": { borderCollapse: "collapse" },
    "& ul, & ol": { paddingInlineStart: "1.5em" },
  },
});

export function EditorQuestionView({ block, placeholders }: QuestionViewProps) {
  const html = stripUnwantedStyles(replacePlaceholders(block.question.html ?? "", placeholders));

  return (
    <PrintBlock>
      <EditorHtml>
        <SafeHTML html={html} />
      </EditorHtml>
    </PrintBlock>
  );
}
